import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import CheckListReportDetailsName, {openCheckListReportDetails} from "../checkLists/checkListReportDetails.controller";
import {States, Strings} from "../../shared/config/constant.module";
import template from './hourlyChecklistReport.tpl.html';
import {StateParams} from "../../common";
import {Plant} from "../../models/navigation";
import {apsCommon} from "../../shared/dto/aps.common";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import ReportServiceName, {ReportService} from "../../services/reports/report.service";
import CheckListsServiceName, {CheckListsService} from "../../services/checkLists/checkLists.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import LocationsServiceName, {LocationsService, PlantLocation} from "../../services/locations/locations.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import {apsEnums} from "../../shared/dto/aps.enums";

let requires = [CheckListReportDetailsName, LoaderServiceName, ReportServiceName, CheckListsServiceName,
  LocationsServiceName, NavigationServiceName];

interface HourlyChecklistReportParams extends StateParams {
  reportPlantId: string;
  locationId: string;
  reportDate: string;
  reportEndDate: string;
  checkListId: string;
  failuresOnly: string;
  completedOnly: string;
  multipleDays: string;
  autoRun: boolean;
}

interface HourlyChecklistReportController {
  plants: Plant[];
  selectedPlant: Plant;
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
  checkLists: apsCommon.IdAndName[];
  selectedChecklistId: string;
  reportDate: Date;
  reportEndDate: Date;
  multipleDays: boolean;
  failuresOnly: boolean;
  completedOnly: boolean;
  form: angular.IFormController;
  report: apsViewModels.HourlyCheckListReport;

  generateReport(): void;
  isFail(val: apsViewModels.HourlyCheckListReportItemValue): boolean;
  isPass(val: apsViewModels.HourlyCheckListReportItemValue): boolean;
  getResult(val: apsViewModels.HourlyCheckListReportItemValue): string;
  updateLocation(): void;
  updatePlant(): void;
  updateDate(): void;
  updateEndDate(): void;
  viewDetails(val: apsViewModels.HourlyCheckListReportItemValue): void;
  updateMultipleDays(): void;
}

class HourlyChecklistReportCtrl implements HourlyChecklistReportController {
  plants: Plant[];
  selectedPlant: Plant;
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
  checkLists: apsCommon.IdAndName[];
  selectedChecklistId: string;
  reportDate: Date;
  reportEndDate: Date;
  multipleDays: boolean;
  failuresOnly: boolean;
  completedOnly: boolean;
  form: angular.IFormController;
  report: apsViewModels.HourlyCheckListReport;

  loaderService: LoaderService;
  reportService: ReportService;
  checkListsService: CheckListsService;
  $q: angular.IQService;
  $state: angular.ui.IStateService;
  $modal: mm.foundation.modal;
  private locationsService: LocationsService;
  private $stateParams: HourlyChecklistReportParams;
  private $timeout: angular.ITimeoutService;

  constructor(
    navigationService: NavigationService,
    loaderService: LoaderService,
    reportService: ReportService,
    checkListsService: CheckListsService,
    locationsService: LocationsService,
    $state: angular.ui.IStateService,
    $stateParams: HourlyChecklistReportParams,
    $q: angular.IQService,
    $timeout: angular.ITimeoutService,
    $modal: mm.foundation.modal) {

    this.loaderService = loaderService;
    this.reportService = reportService;
    this.checkListsService = checkListsService;
    this.$q = $q;
    this.$state = $state;
    this.$modal = $modal;
    this.checkLists = [];
    this.locationsService = locationsService;
    this.reportDate = moment().startOf('day').toDate();
    this.reportEndDate = moment().startOf('day').toDate();
    this.failuresOnly = false;
    this.multipleDays = false;
    this.completedOnly = false;
    this.$stateParams = $stateParams;
    this.$timeout = $timeout;

    var promise = locationsService.initialize().then(() => {
      return navigationService.getNavigation().then((nav) => {
        this.plants = nav.plants();
        this.selectedPlant = nav.selectedPlant;
        this.updatePlant(true);

        this.loadState($stateParams);

        return this.loadCheckLists().then(() => {
          return this.loadState2($stateParams);
        });
      });
    });

    loaderService.loadingPromise(promise);
  }

  private getPlantId(): string {
    return this.selectedPlant ? this.selectedPlant.id : null;
  }

  isFail(val: apsViewModels.HourlyCheckListReportItemValue): boolean {
    return val && val.status === apsEnums.CheckStatus.Failed;
  }

  isPass(val: apsViewModels.HourlyCheckListReportItemValue): boolean {
    return val && val.status === apsEnums.CheckStatus.Passed;
  }

  getResult(val: apsViewModels.HourlyCheckListReportItemValue): string {
    if (!val || !val.status || val.status === apsEnums.CheckStatus.NotDone) {
      return '';
    }

    if ((val.result || '').length === 0) {
      return 'N/A';
    }

    if ((<any>val.result) instanceof Date) {
      return DateUtilityService.formatTime(<Date>(<any>val.result), this.report.plantTimeZoneId);
    }

    return val.result;
  }

  updatePlant(skipChecklists?: boolean): void {
    this.selectedLocation = undefined;
    this.locations = this.locationsService.getPlantLocations(this.selectedPlant);
    if (skipChecklists !== true) {
      this.loadCheckLists(true);
    }
  }

  formatTime(time: Date|moment.Moment, timeZoneId: string): string {
    return DateUtilityService.formatTimeOnly(time, timeZoneId);
  }

  updateLocation(): void {
    this.loadCheckLists(true);
  }

  updateDate(): void {
    this.loadCheckLists(true);
  }

  updateEndDate(): void {
    this.loadCheckLists(true);
  }

  updateMultipleDays(): void {
    this.report = null;
  }

  private loadCheckListTimeout: angular.IPromise<any>;

  private loadCheckLists(throttle?: boolean): angular.IPromise<any> {
    if (this.loadCheckListTimeout) {
      this.$timeout.cancel(this.loadCheckListTimeout);
      this.loadCheckListTimeout = null;
    }

    this.loadCheckListTimeout = this.$timeout(() => {
      var criteria: apsViewModels.CheckListDropDownCriteria = {
        plantId: this.getPlantId(),
        locationType: undefined,
        locationId: undefined,
        startDate: this.reportDate,
        endDate: moment(this.multipleDays ? this.reportEndDate : this.reportDate).add(1, 'day').toDate()
      };
      if (this.selectedLocation) {
        criteria.locationType = this.selectedLocation.locationType;
        criteria.locationId = this.selectedLocation.id;
      }
      this.loadCheckListTimeout = null;
      return this.checkListsService.getCheckListsWithData(criteria)
        .then(data => this.checkLists = data);
    }, throttle ? 500 : null);
    return this.loadCheckListTimeout;
  }

  loadState(params: HourlyChecklistReportParams): void {
    if (params.reportPlantId && params.locationId && params.locationId !== params.shopId) {
      var plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        var location = _.find(this.locations, { id: params.locationId });
        if (location) {
          this.selectedPlant = plant;
          this.selectedLocation = location;
        }
      }
    }

    if (!this.selectedLocation) {
      this.selectedLocation = _.find(this.locations, { id: params.shopId });
    }

    if (params.checkListId) {
      this.selectedChecklistId = params.checkListId;
    }

    if (params.reportDate) {
      var reportDate = moment(params.reportDate);
      if (reportDate.isValid()) {
        this.reportDate = reportDate.toDate();
      }
    }

    if (params.reportEndDate) {
      var reportEndDate = moment(params.reportEndDate);
      if (reportEndDate.isValid()) {
        this.reportEndDate = reportEndDate.toDate();
      }
    }

    this.failuresOnly = (params.failuresOnly === 'true');
    this.multipleDays = (params.multipleDays === 'true');
    this.completedOnly = (params.completedOnly === 'true');
  }

  loadState2(params: HourlyChecklistReportParams): angular.IPromise<any> {
    if (params.autoRun && !this.multipleDays) {
      return this.runCheckListReport();
    }

    return this.$q.when();
  }

  saveState(): void {
    var locationId = this.selectedLocation ? this.selectedLocation.id : undefined;
    if (locationId === this.$stateParams.shopId) {
      locationId = undefined;
    }
    var reportDate = this.reportDate ? this.reportDate.toISOString() : undefined;
    var params: HourlyChecklistReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: (locationId && this.selectedPlant) ? this.selectedPlant.id : undefined,
      locationId: locationId,
      reportDate: reportDate,
      multipleDays: this.multipleDays.toString(),
      reportEndDate: (this.multipleDays && this.reportEndDate) ? this.reportEndDate.toISOString() : reportDate,
      checkListId: this.selectedChecklistId,
      failuresOnly: this.failuresOnly.toString(),
      completedOnly: this.completedOnly.toString(),
      autoRun: true
    };

    this.$state.transitionTo(this.$state.current.name, params);
  }

  generateReport() {
    if (this.form && this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runCheckListReport());
  }

  private runCheckListReport(): angular.IPromise<any> {
    this.report = null;

    var plantId = this.getPlantId();
    var locationType = null;
    var locationId = null;
    if (this.selectedLocation) {
      locationType = this.selectedLocation.locationType;
      locationId = this.selectedLocation.id;
    }
    var checkListId = this.selectedChecklistId;

    return this.reportService
      .getHourlyChecklistReport(plantId, locationType, locationId,
        checkListId, this.reportDate, this.failuresOnly, this.multipleDays ? this.reportEndDate : this.reportDate,
        this.completedOnly)
      .then( (report: apsViewModels.HourlyCheckListReport) => {
        this.report = report;
      });
  }

  viewDetails(val: apsViewModels.HourlyCheckListReportItemValue): void {
    if (val && val.checkListInstanceId) {
      var promise = this.checkListsService
        .getCheckListReportDetail(val.checkListInstanceId, val.plantLocationId, val.scheduledOn,
          this.failuresOnly ? apsEnums.CheckListReportFilter.FailedOnly : apsEnums.CheckListReportFilter.AllCheckLists)
        .then((data: apsViewModels.CheckListInstanceReportDetail) => {
        openCheckListReportDetails({
          checkListInstance: data.reportItem,
          detail: data
        }, this.$modal);
      });
      this.loaderService.loadingPromise(promise);
    }
  }
  exportToCsv() {
    let result = [];
    let titles = [];
    let location = [];
    titles.push("Check Name");
    titles.push("Location");
    this.report.columns.forEach(x => {
      var time = this.multipleDays
        ? DateUtilityService.formatTime(x.scheduledOn, this.report.plantTimeZoneId)
        : DateUtilityService.formatTimeOnly(x.scheduledOn, this.report.plantTimeZoneId);
      titles.push(time);
    });
    result.push(titles);
    let source = _.map(this.report.rows, (x) => {
      return  {
        checkName: x.checkName,
        machine: x.machine,
        section: x.section,
        gobName: x.gobName,
        dimensionName: x.dimensionName,
        values : x.values
      };
    });
    source.forEach(x => {
      let rowValues = [];
      x.values.forEach(k => {
        rowValues.push(this.getResult(k));
      });
      result.push({
        checkName: x.checkName,
        machine: (x.machine != null ? x.machine + " " : "") + (x.section != null ? x.section + " " : "") 
        + (x.gobName != null ? x.gobName + " " : "") + (x.dimensionName != null ? x.dimensionName + " " : ""),
        times: rowValues     
      });
    });    
    return result;
  }
}

export default angular.module('aps.controller.hourlyChecklistReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.HOURLY_CHECKLIST_REPORT,
      url: '/admin/reports/hourlyChecklistReport?locationId&reportDate&checkListId&failuresOnly&autoRun&reportPlantId',
      params: {
        locationId: {dynamic: true},
        reportDate: {dynamic: true},
        reportEndDate: {dynamic: true},
        failuresOnly: {dynamic: true},
        completedOnly: {dynamic: true},
        multipleDays: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
        checkListId: {dynamic: true},
      },
      controller: 'HourlyChecklistReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Hourly Check List Report', 
        v2Uri: 'aps/reports/check-list-hourly-report'
      }
    });
  })
  .controller('HourlyChecklistReportCtrl', HourlyChecklistReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/hourlyChecklistReport.controller.ts